import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { RevisionMeta, PrimaryDocuments } from "@/components/shared";

export const ResearchIndexPage = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  
  useEffect(() => {
    let url = "/research";
    if (search.length >= 2) {
      url = `/research/search?q=${encodeURIComponent(search)}`;
    }
    
    api.get(url).then(res => {
      setDocs(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [search]);
  
  return (
    <div className="max-w-6xl mx-auto px-4 py-12" data-testid="research-index-page">
      <h1 className="text-3xl font-bold serif-heading mb-4">Research Library</h1>
      <p className="text-gray-600 mb-4">
        The research workstream operates alongside the archive's growing study corpus, providing 
        evidence-disciplined analysis of claims, entities, and chronology. This library includes 
        primary source registers, topic-specific research guides, and reference documents 
        covering broadcast territories, performance sessions, and charity fundraising records.
      </p>
      <p className="text-gray-500 text-sm mb-8 italic">
        Research outputs require editorial review before integration into public studies. 
        Sources are classified in a five-tier hierarchy from contemporaneous primary evidence 
        down to collector materials used as leads only.
      </p>
      
      <div className="relative max-w-md mb-8">
        <label htmlFor="research-search" className="sr-only">Search research documents</label>
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" />
        <Input
          id="research-search"
          placeholder="Search documents..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="research-search-input"
        />
      </div>
      
      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
      ) : docs.length === 0 ? (
        <div className="text-center py-16 bg-[#F7F1E5] rounded-lg border border-[#D8CDBD]">
          {search.trim() ? (
            <>
              <p className="text-gray-600 mb-2">No records match the current search.</p>
              <button 
                onClick={() => setSearch('')}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Clear search
              </button>
            </>
          ) : (
            <p className="text-gray-600">No evidence-backed records have been published in this category.</p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {docs.map((doc) => (
            <Link 
              key={doc.id} 
              to={`/research/${doc.slug}`}
              className="archive-card p-5 flex items-start justify-between group block hover:border-[#3A7A8E] focus:outline-none focus:ring-2 focus:ring-[#3A7A8E] focus:ring-offset-2 transition-colors"
              data-testid={`research-doc-${doc.slug}`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-xs">{doc.doc_type}</Badge>
                  <span className="text-xs text-gray-500">{doc.date}</span>
                </div>
                <h3 className="font-semibold serif-heading group-hover:text-blue-600 transition-colors">
                  {doc.title}
                </h3>
                <p className="text-sm text-gray-500 mt-1">{doc.publisher}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 ml-4 flex-shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export const ResearchDetailPage = () => {
  const { slug } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    api.get(`/research/${slug}`).then(res => {
      setDoc(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);
  
  if (loading) {
    return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }
  
  if (!doc) {
    return <div className="max-w-4xl mx-auto px-4 py-12"><p>Document not found</p></div>;
  }
  
  return (
    <div className="max-w-4xl mx-auto px-4 py-12" data-testid="research-detail-page">
      <Link to="/research" className="text-sm text-blue-600 hover:text-blue-800 mb-4 inline-flex items-center gap-1">
        ← Back to Research Library
      </Link>
      
      <article>
        <header className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline">{doc.doc_type}</Badge>
            <span className="text-sm text-gray-500">{doc.date}</span>
          </div>
          <h1 className="text-3xl font-bold serif-heading mb-2">{doc.title}</h1>
          <p className="text-gray-500">{doc.publisher}</p>
        </header>
        
        {doc.abstract && (
          <section className="mb-8">
            <h2 className="text-lg font-semibold serif-heading mb-3">Abstract</h2>
            <p className="text-gray-600 leading-relaxed">{doc.abstract}</p>
          </section>
        )}
        
        {/* Primary Documents */}
        <PrimaryDocuments 
          entityType="research" 
          entityId={doc.id}
          title="Full Document"
        />
        
        <RevisionMeta updatedAt={doc.updated_at} />
      </article>
    </div>
  );
};

export default ResearchIndexPage;
